import { useEffect } from "react";
import { UserType, onSocketEvent } from "@jasper/shared";
import { UseAuth } from "../../../contexts";
import { AssignNewProviderEvent, OrderItem } from "../types";
import { OrderStatus } from "../../../generated/graphql";

interface Props {
  addNewOrder: (newOrder: OrderItem) => void;
  removeOrder: (orderToRemove: Pick<OrderItem, "id">) => void;
  page: number;
  filteredStatuses: OrderStatus[];
}

export const useSocketEvents = ({
  addNewOrder,
  removeOrder,
  page,
  filteredStatuses = [],
}: Props) => {
  const { user } = UseAuth();

  if (!user) {
    throw new Error("useSocketEvents can only be used in a logged context");
  }

  // These events reach the admin-pastilles and provider-pastilles-<group> rooms,
  // joined by useNavbarNotifications, which the layout always mounts
  useEffect(() => {
    if (user.type === UserType.JASPER) {
      const handleNewOrder = (args: { order: OrderItem }) => {
        const isFirstPage = page === 1;

        const isRelevantStatus =
          !filteredStatuses?.length ||
          filteredStatuses.includes(args.order.status);

        if (isFirstPage && isRelevantStatus) {
          addNewOrder(args.order);
        }
      };

      return onSocketEvent("newOrderConfirmed", handleNewOrder);
    }

    if (user.type === UserType.SUPPLIER) {
      const handleAssignNewProvider = (args: AssignNewProviderEvent) => {
        // The websocket-api never sends an event without an order, but removeOrder
        // would crash on one: ignore it rather than trust every emitter
        if (!args?.order) {
          return;
        }

        if (user?.userGroupId === args.order.providerGroupId) {
          // Its current provider gets the whole order
          const order = args.order as OrderItem;
          const isFirstPage = page === 1;
          const isMatchingStatus =
            !filteredStatuses?.length ||
            filteredStatuses.includes(order.status);

          if (isFirstPage && isMatchingStatus) {
            addNewOrder(order);
          }
        } else {
          // Also the previous provider of a reassigned order, which only gets its id
          removeOrder(args.order);
        }
      };

      return onSocketEvent("assignNewProvider", handleAssignNewProvider);
    }
  }, [page, filteredStatuses, user]);
};
